import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import cn from 'classnames';
import { subMonths, subDays, startOfMonth, endOfMonth, format, isAfter } from 'date-fns';
import { isMobile } from 'react-device-detect';
import { toastr } from 'react-redux-toastr';

import { fetchCalendarHosLogsAction, getCalendarHosLogs } from 'src/features/hosLogs/store';
import { getUserId } from 'src/features/profile/store';
import { getHighlightedDays } from 'src/features/hosLogs/store/utils';
import { fetchCalendarDotsLogsCount } from 'src/configs/gui-config';

// Utils
import { getDate6MonthsBack } from 'src/utils/dateUtils';

import 'react-datepicker/dist/react-datepicker.css';
import './RangeDatepicker.scss';

interface RangeDatepickerProps {
  onChangeRangeFilterHandler: ([startRange, endRange]: [Date, Date]) => void;
  startDate: Date | null;
  endDate: Date | null;
}

export const RangeDatepicker: React.FC<RangeDatepickerProps> = ({
  startDate,
  endDate,
  onChangeRangeFilterHandler,
}) => {
  const [chosenPeriod, setChosenPeriod] = useState(0);
  const [animateTabClassName, setAnimateTabClassName] = useState(false);
  const [isInitial, setIsInitial] = useState(true);

  const dispatch = useDispatch();
  const calendarHosLogs = useSelector(getCalendarHosLogs);
  const employeeId = useSelector(getUserId);

  const periods = ['Day', 'Week', 'Month'];

  useEffect(() => {
    let timerID: number;
    if (!isInitial) {
      setAnimateTabClassName(true);
      timerID = window.setTimeout(function() {
        setAnimateTabClassName(false);
      }, 100);
    }
    return (): void => clearTimeout(timerID);
  }, [chosenPeriod, isInitial]);

  useEffect(() => {
    setIsInitial(false);
  }, []);

  useEffect(() => {
    if (employeeId) {
      dispatch(
        fetchCalendarHosLogsAction({
          startDate: format(startOfMonth(subMonths(new Date(), 6)), 'yyyy-MM-dd'),
          endDate: format(new Date(), 'yyyy-MM-dd'),
          employeeIds: employeeId,
          count: fetchCalendarDotsLogsCount,
          startIndex: 0,
        }),
      );
    }
  }, [employeeId, dispatch]);

  const onSelectTabHandler = useCallback(
    (index: number) => {
      return (): void => {
        setChosenPeriod(index);
      };
    },
    [setChosenPeriod],
  );

  const onChangeRange = useCallback(
    ([startRange, endRange]: [Date, Date]) => {
      if (isAfter(getDate6MonthsBack(), startRange)) {
        toastr.error('Error', 'Log Not Available.');
        return;
      }
      chosenPeriod === 2 &&
        endRange &&
        (endRange.getMonth() === new Date().getMonth()
          ? (endRange = new Date())
          : (endRange = endOfMonth(endRange)));
      onChangeRangeFilterHandler([startRange, endRange]);
    },
    [onChangeRangeFilterHandler, chosenPeriod],
  );

  const onChangeWeekRange = useCallback(
    ([startRange, endRange]: [Date, Date]) => {
      endRange = startRange;
      startRange = subDays(endRange, 7);
      onChangeRangeFilterHandler([startRange, endRange]);
    },
    [onChangeRangeFilterHandler],
  );

  const highlightedDays = useMemo(() => getHighlightedDays(calendarHosLogs), [calendarHosLogs]);

  // -------------------------------
  // TODO this should be refactored after react-datepicker component issue will be fixed
  const [highlightedMonths, setHighlightedMonths] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear().toString());

  const datepickerRef = useRef<HTMLDivElement | null>(null);
  const datepickerYearHeader = datepickerRef.current
    ? datepickerRef.current.querySelector('.react-datepicker-year-header')
    : null;

  const datepickerChosenYear = useMemo(
    () => datepickerYearHeader && datepickerYearHeader.innerHTML,
    [datepickerYearHeader],
  );

  const addHighlightedMonthClass = useCallback(() => {
    if (datepickerRef.current && highlightedMonths) {
      const months = datepickerRef.current.querySelectorAll('.react-datepicker__month-text');
      months.forEach(item => {
        item.classList.remove('react-datepicker-events-month');
      });
      highlightedMonths.forEach(item => {
        const monthForHighlight =
          datepickerRef.current &&
          datepickerRef.current.querySelector(`.react-datepicker__month-${item}`);
        monthForHighlight && monthForHighlight.classList.add('react-datepicker-events-month');
      });
    }
  }, [datepickerRef, highlightedMonths]);

  const handleOnSelectDatepicker = useCallback(() => {
    setTimeout(addHighlightedMonthClass);
  }, [addHighlightedMonthClass]);

  const handleOnYearChangeDatepicker = useCallback(date => {
    setCurrentYear(date.getFullYear().toString());
  }, []);

  useEffect(() => {
    setHighlightedMonths(highlightedDays.highlightedMonths[currentYear]);
  }, [currentYear, highlightedDays]);

  useEffect(() => {
    datepickerChosenYear && setCurrentYear(datepickerChosenYear);
  }, [datepickerChosenYear]);

  useEffect(() => {
    addHighlightedMonthClass();
  }, [highlightedMonths, addHighlightedMonthClass, chosenPeriod]);
  // -------------------------------

  return (
    <div className={cn('range-datepicker', { 'is-desktop': !isMobile })}>
      <div className="range-datepicker__top-row">
        <div className={cn('range-datepicker__date-text', { 'is-full': startDate })}>
          {startDate && format(startDate, 'MMM, dd, yyyy')}
        </div>
        <div>—</div>
        <div className={cn('range-datepicker__date-text', { 'is-full': endDate })}>
          {endDate && format(endDate, 'MMM, dd, yyyy')}
        </div>
      </div>
      <div className="range-datepicker__tab-row">
        {periods.map((title: string, index: number) => {
          return (
            <div
              key={`${title}-${index}`}
              className={chosenPeriod === index ? 'is-active' : ''}
              onClick={onSelectTabHandler(index)}
            >
              {title}
            </div>
          );
        })}
      </div>
      <div
        ref={datepickerRef}
        className={cn('range-datepicker__body', { 'animate-tab': animateTabClassName })}
      >
        <DatePicker
          selected={startDate}
          onChange={chosenPeriod === 1 ? onChangeWeekRange : onChangeRange}
          startDate={startDate}
          endDate={endDate}
          selectsRange
          inline
          minDate={startOfMonth(subMonths(new Date(), 6))}
          maxDate={new Date()}
          showMonthYearPicker={chosenPeriod === 2}
          highlightDates={highlightedDays.highlightedDays}
          onYearChange={handleOnYearChangeDatepicker}
          onSelect={handleOnSelectDatepicker}
        />
      </div>
    </div>
  );
};
